import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { games } from "@/mocks/games";

export default function GameDetail() {
  const { slug } = useParams();
  const game = games.find((g) => g.slug === slug);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    if (game) {
      document.title = `${game.title} 下载 - 火影同人游戏库`;
    }
  }, [game]);

  if (!game) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background-50">
        <Navbar />
        <p className="text-foreground-700">未找到该游戏资源</p>
        <Link to="/" className="mt-4 text-primary-600 hover:underline">
          返回首页
        </Link>
      </div>
    );
  }

  const related = games.filter((g) => g.slug !== game.slug).slice(0, 3);

  const jsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: game.title,
    description: game.intro,
    image: game.cover,
    inLanguage: "zh-CN",
    mainEntityOfPage: `https://gzgggs.cn/game/${game.slug}`,
    dateModified: game.update,
    author: { "@type": "Organization", name: "火影同人游戏库" },
  });

  const handleDownload = () => {
    setShowTip(true);
    window.setTimeout(() => setShowTip(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      <Navbar />

      <main className="pt-20">
        <nav aria-label="面包屑" className="mx-auto max-w-6xl px-4 md:px-6 pt-6">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li>
              <Link to="/#library" className="hover:text-primary-600">
                游戏资源
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li className="text-foreground-800">{game.title}</li>
          </ol>
        </nav>

        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src={game.cover}
                  alt={`${game.title} 游戏截图与封面`}
                  title={`${game.title} 下载`}
                  className="h-auto w-full object-cover object-top"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-white">
                  {game.category}
                </span>
                {game.noInstall && (
                  <span className="rounded-md bg-secondary-500 px-2.5 py-1 text-xs font-bold text-white">
                    免安装
                  </span>
                )}
                {game.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="font-heading mt-4 text-3xl font-black text-foreground-950 md:text-4xl">
                {game.title}
              </h1>
              <p className="mt-4 text-base leading-8 text-foreground-600">{game.intro}</p>

              <div className="mt-6 grid grid-cols-2 gap-4 rounded-lg bg-background-100 border border-background-200 p-5 sm:grid-cols-3">
                {[
                  { label: "游戏体积", value: game.size },
                  { label: "当前版本", value: game.version },
                  { label: "更新时间", value: game.update },
                  { label: "累计下载", value: game.downloads },
                  { label: "玩家评分", value: `${game.rating} / 5` },
                  { label: "人气值", value: `${game.hot}` },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 text-sm font-bold text-foreground-900">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  <i className="ri-download-2-line"></i>
                  立即下载
                </button>
                <button
                  type="button"
                  onClick={() =>
                    document.getElementById("steps")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-8 py-3.5 text-base font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                >
                  <i className="ri-book-open-line"></i>
                  查看安装步骤
                </button>
              </div>

              {showTip && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-shield-check-line"></i>
                  下载已记录，请在对应平台渠道获取安装包，并先进行安全检查。
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-download-cloud-line text-primary-500"></i>
                  下载方式：网盘 + 直链
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-cpu-line text-primary-500"></i>
                  运行：{game.platform.join(" / ")}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <section>
                <h2 className="font-heading flex items-center gap-2 text-2xl font-bold text-foreground-950">
                  <i className="ri-file-list-3-line text-primary-500"></i>
                  游戏介绍
                </h2>
                <div className="prose-rich mt-4">
                  {game.description.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </section>

              <section className="mt-10">
                <h2 className="font-heading flex items-center gap-2 text-2xl font-bold text-foreground-950">
                  <i className="ri-star-line text-primary-500"></i>
                  游戏特色
                </h2>
                <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {game.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-700"
                    >
                      <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500"></i>
                      {f}
                    </li>
                  ))}
                </ul>
              </section>

              <section id="steps" className="mt-10">
                <h2 className="font-heading flex items-center gap-2 text-2xl font-bold text-foreground-950">
                  <i className="ri-install-line text-primary-500"></i>
                  安装与启动步骤
                </h2>
                <ol className="mt-4 space-y-4">
                  {game.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="font-heading flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="rounded-md bg-background-100 px-4 py-3 text-sm leading-7 text-foreground-700">
                        {s}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="mt-10">
                <h2 className="font-heading flex items-center gap-2 text-2xl font-bold text-foreground-950">
                  <i className="ri-hard-drive-2-line text-primary-500"></i>
                  配置要求
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {game.requirements.map((r) => (
                    <li
                      key={r}
                      className="flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-700"
                    >
                      <i className="ri-information-line mt-0.5 text-secondary-600"></i>
                      {r}
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="rounded-lg bg-primary-100/60 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-primary-900">
                    <i className="ri-download-2-line"></i>
                    下载说明
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-primary-900/80">
                    点击右上角「立即下载」进入对应平台渠道获取安装包。安装前请确认设备满足上面的配置要求。
                  </p>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-download-2-line"></i>
                    获取 {game.category}
                  </button>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="font-heading text-lg font-bold text-foreground-950">
                    支持平台
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {game.platform.map((p) => (
                      <span
                        key={p}
                        className="rounded bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14">
            <h2 className="font-heading flex items-center gap-2 text-2xl font-bold text-foreground-950">
              <i className="ri-thunderstorms-line text-primary-500"></i>
              相关游戏推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/game/${r.slug}`}
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-all hover:-translate-y-1 hover:border-primary-200"
                >
                  <div className="overflow-hidden">
                    <img
                      src={r.cover}
                      alt={`${r.title}封面`}
                      title={`${r.title} 下载`}
                      className="h-40 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-600">
                      {r.shortTitle}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-foreground-500">
                      {r.intro}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}